import _isEqual from 'lodash/isEqual';

/**
 * For form dirty check operations
 *
 * RJSF (@rjsf/core) by default do not provide a functionality to identify
 * whether the form is dirty/pristine.
 * So, this class will be the utility for RJSF wrapper <Form>.
 *
 * Additionally,
 * we need to handle a flag 'isDataInitialized' as the RJSF's form automatically
 * triggers a onChange callback initially. To not consider that as a dirty(change) operation,
 * we considered it a data initialization callback. And then further onChange calls
 * will decide the form's dirty state by comparing the initial and current form data.
 *
 * @type {class}
 */
class FormDirtyCheck {
    /**
     * Is data initialized
     *
     * @type {boolean}
     */
    isDataInitialized = false;

    /**
     * Is form dirty (changed)
     *
     * @type {boolean}
     */
    isDirty = false;

    /**
     * Initial form data defined after data initialization
     *
     * @type {*}
     */
    initialFormData = {};

    /**
     * Current form data
     *
     * @type {*}
     */
    formData = {};

    /**
     * Checks whether the initialFormData and formData are not equal
     *
     * @return {boolean}
     */
    isFormDataChanged = () => _isEqual(this.formData, this.initialFormData);

    /**
     * Calls when formData change
     *
     * RJSF v5 fires onChange multiple times during mount (e.g. default
     * resolution, liveValidate transition). To avoid false-positive dirty
     * detection we keep resetting the baseline until the host Form
     * component has finished mounting (indicated by `isFormMounted`).
     *
     * @param   {Object}        form object
     * @param   {boolean}       isFormMounted - true once the Form wrapper's
     *                          componentDidMount has executed
     */
    onChange = ({ formData }, isFormMounted = true) => {
        this.formData = formData;

        if (!isFormMounted) {
            // Still in the initialization phase — keep updating the
            // baseline so that post-mount data is compared against the
            // fully-resolved initial state.
            this.initialFormData = formData;
            return;
        }
        if (this.isDataInitialized) {
            this.isDirty = !this.isFormDataChanged();
        } else {
            // Only capture a new baseline if the pre-mount phase
            // did not already set one.
            if (_isEqual(this.initialFormData, {})) {
                this.initialFormData = formData;
            }
            this.isDataInitialized = true;
            this.isDirty = !this.isFormDataChanged();
        }
    };

    /**
     * Initial form data setter
     *
     * @param   {Object}    formData
     */
    setInitialFormData = formData => {
        this.initialFormData = formData;
    };

    /**
     * Form data setter
     *
     * @param   {Object}    formData
     */
    setFormData = formData => {
        this.formData = formData;
    };

    /**
     * Check whether the form is dirty
     *
     * @return {boolean}
     */
    isFormDirty = () => this.isDirty;
}

export default FormDirtyCheck;
